import {
  DateTimeFormatter,
  FormatterWithPrecision,
  type TimeFrameFormatter,
} from '../../core/types/formatter.js';
import { ScaleTypesMap } from '../types/scale-types-map.js';
import type { DotSeriesContent, XYChartExploreState } from '../types/state.js';
import {
  RECT_VARIANT,
  RectSeriesWithDatapoints,
  DOT_VARIANT,
  DotSeriesWithDatapoints,
  XYChartRectSeriesInternal,
  XYChartDotSeriesInternal,
  XYChartSeriesWithDatapoints,
  XYChartVariantsInternal,
  AnyXYChartScale,
  type RectDatapointInternal,
} from '../types/xy-chart-internals.js';
import { XYAccessorDataTypes, type DotDatapoint } from '../types/xy-chart.js';

interface BaseGuard {
  v0: XYAccessorDataTypes;
}

function isNumerical<T extends BaseGuard>(args: T): args is T {
  return typeof args.v0 === 'number';
}

function isCategorical<T extends BaseGuard>(args: T): args is T {
  return typeof args.v0 === 'string';
}

function isTime<T extends BaseGuard>(args: T): args is T {
  return args.v0 instanceof Date;
}

interface HoveringPointGuard extends BaseGuard {
  scale: AnyXYChartScale;
}

export function isNumericalHoveringPoint(
  args: HoveringPointGuard,
): args is ScaleTypesMap['numerical.linear']['hoveringPoint'] {
  return isNumerical(args);
}

export function isTimeHoveringPoint(
  args: HoveringPointGuard,
): args is ScaleTypesMap['time']['hoveringPoint'] {
  return isTime(args);
}

export function isCategoricalHoveringPoint(
  args: HoveringPointGuard,
): args is ScaleTypesMap['categorical']['hoveringPoint'] {
  return isCategorical(args);
}

interface DotSeriesScaleGuard extends BaseGuard {
  v1: Date | undefined;
  scale: AnyXYChartScale;
}

export function isNumericalDotScale(
  args: DotSeriesScaleGuard,
): args is ScaleTypesMap['numerical.linear']['dotScale'] {
  return isNumerical(args);
}

export function isTimeDotScale(
  args: DotSeriesScaleGuard,
): args is ScaleTypesMap['time']['dotScale'] {
  return isTime(args);
}

export function isCategoricalDotScale(
  args: DotSeriesScaleGuard,
): args is ScaleTypesMap['categorical']['dotScale'] {
  return isCategorical(args);
}

interface RectScaleGuard extends BaseGuard {
  scale: AnyXYChartScale;
  v1: XYAccessorDataTypes | undefined;
}
export function isNumericalRectScale(
  args: RectScaleGuard,
): args is ScaleTypesMap['numerical.linear']['rectScale'] {
  return isNumerical(args);
}

export function isTimeRectScale(
  args: RectScaleGuard,
): args is ScaleTypesMap['time']['rectScale'] {
  return isTime(args);
}

export function isCategoricalRectScale(
  args: RectScaleGuard,
): args is ScaleTypesMap['categorical']['rectScale'] {
  return isCategorical(args);
}

export type TooltipGuard = BaseGuard &
  (
    | {
        formatter:
          | FormatterWithPrecision
          | DateTimeFormatter
          | TimeFrameFormatter
          | undefined;
      }
    | {
        formatter:
          | FormatterWithPrecision
          | DateTimeFormatter
          | TimeFrameFormatter
          | undefined;
        v1: XYAccessorDataTypes;
      }
  );

export function isNumericalToolTip<
  T1 extends TooltipGuard,
  T2 extends T1 & ScaleTypesMap['numerical.linear']['tooltip'],
>(args: T1): args is Pick<T2, keyof T1> {
  return isNumerical(args);
}

export function isTimeTooltip<
  T1 extends TooltipGuard,
  T2 extends T1 & ScaleTypesMap['time']['tooltip'],
>(args: T1): args is Pick<T2, keyof T1> {
  return isTime(args);
}

export function isCategoricalTooltip<
  T1 extends TooltipGuard,
  T2 extends T1 & ScaleTypesMap['categorical']['tooltip'],
>(args: T1): args is Pick<T2, keyof T1> {
  return isCategorical(args);
}

export function isRectSeries(
  series: XYChartVariantsInternal,
): series is XYChartRectSeriesInternal;

export function isRectSeries(
  series: XYChartSeriesWithDatapoints,
): series is RectSeriesWithDatapoints;

export function isRectSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartRectSeriesInternal | RectSeriesWithDatapoints {
  return series.type === RECT_VARIANT;
}

export function isDotSeries(
  series: XYChartVariantsInternal,
): series is XYChartDotSeriesInternal;

export function isDotSeries(
  series: XYChartSeriesWithDatapoints,
): series is DotSeriesWithDatapoints;

export function isDotSeries(
  series: XYChartVariantsInternal | XYChartSeriesWithDatapoints,
): series is XYChartDotSeriesInternal | DotSeriesWithDatapoints {
  return series.type === DOT_VARIANT;
}

export function isDotSeriesTooltipContent(
  content: XYChartExploreState['content'],
): content is DotSeriesContent {
  return content?.series?.type === DOT_VARIANT;
}

export function isRectDatapoint(
  datapoint: RectDatapointInternal | DotDatapoint,
): datapoint is RectDatapointInternal {
  return 'value' in datapoint;
}
